// src/components/ui/Dropdown.jsx
//
// Primitives layer, phase 1. Extracts the trigger-button + positioned-
// panel + click-outside-to-close mechanic already correctly implemented
// in profileDropDown/dropDown.jsx (proper ARIA roles, focus-visible
// rings) -- that file's own menu-item markup was already right, this
// just generalizes the open/close plumbing around it so every other
// dropdown in the app (notifications, action menus) doesn't reimplement
// the same click-outside listener independently.
import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * @param {(props: {open: boolean, toggle: () => void}) => React.ReactNode} trigger
 * @param {'left'|'right'} align - which edge of the trigger the panel hangs from
 * @param {string} panelClassName - extra classes for the panel container
 */
export default function Dropdown({
  trigger,
  children,
  align = 'right',
  panelClassName = '',
  panelRole = 'menu',
  panelAriaLabel,
  open: controlledOpen,
  onOpenChange,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    (next) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpen]);

  const toggle = () => setOpen(!open);

  return (
    <div className="relative" ref={containerRef}>
      {trigger({ open, toggle })}
      {open && (
        <div
          role={panelRole}
          aria-label={panelAriaLabel}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-2 bg-white border border-gray-200 dark:bg-gray-900 dark:border-gray-800 rounded-lg shadow-xl z-50 ${panelClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
